import { createContext, useContext, type ReactNode } from "react";
import type { Brand } from "../theme/brand";
import type { NavItem } from "./nav";

interface ShellConfig {
  brand: Brand;
  nav: NavItem[];
}

const ShellContext = createContext<ShellConfig | null>(null);

/**
 * Carries the app's brand and nav manifest down to the shell.
 *
 * The shell can't take them as props: it renders *inside* the router (so its
 * navigation links have a router context to read), while brand and nav are
 * declared *outside* it at the app's entry point. A context is what bridges
 * the two without every route having to thread them through.
 */
export function ShellConfigProvider({
  brand,
  nav,
  children,
}: ShellConfig & { children: ReactNode }) {
  return <ShellContext.Provider value={{ brand, nav }}>{children}</ShellContext.Provider>;
}

export function useShellConfig(): ShellConfig {
  const config = useContext(ShellContext);
  if (!config) {
    throw new Error("useShellConfig must be used inside an AppRoot");
  }
  return config;
}
